import identity from 'lodash/identity';
import { useCallback } from 'react';

import { MapExtent } from './GisTypes';

export type Cfg = {
  baseUrl: string;
  token?: string;
  onErr?: (err: InvalidTokenError | unknown) => void;
};

export class InvalidTokenError extends Error {
  // @ts-ignore
  private response: Response;
  constructor(res: Response) {
    super();

    this.response = res;
  }
}

const load = async (url: string, onErr: Cfg['onErr']): Promise<any> => {
  try {
    const res = await fetch(url);

    if (res.status === 498) {
      throw new InvalidTokenError(res);
    }

    return await res.json();
  } catch (err) {
    onErr && onErr(err);
    return null;
  }
};

export const useGISFetcher = ({ baseUrl, token, onErr = identity }: Cfg) => {
  const filterQueryParamsByValue = useCallback(
    <T extends Record<string, string>>(queryParams: T): T =>
      Object.entries(queryParams).reduce((finalQueryParams, currentQueryParam) => {
        const [queryName, queryValue] = currentQueryParam;
        if (!queryValue) return finalQueryParams;

        return {
          ...finalQueryParams,
          [queryName]: queryValue,
        };
      }, {} as T),
    [],
  );

  const createQueryString = useCallback(
    <T extends Record<string, string>>(queryParams: T): string => {
      const filteredQueryParamsByValue = filterQueryParamsByValue(queryParams);
      const queryString = new URLSearchParams(filteredQueryParamsByValue).toString();
      const validQueryForGIS = queryString
        .split('&')
        .map((query) => {
          const [queryName, queryValue] = query.split('=');

          return `${queryName} LIKE '%${queryValue}%'`;
        })
        .join(' AND ');

      return encodeURIComponent(validQueryForGIS);
    },
    [filterQueryParamsByValue],
  );

  const loadList = useCallback(
    async (queryParams: Record<string, string> = {}) => {
      const url = `${baseUrl}?f=geoJSON&outFields=*&token=${sessionStorage.getItem('gis.token') || token}&where=${createQueryString(queryParams) || '1=1'}`;

      const list = await load(url, onErr);
      const extentResponse: Record<'extent', MapExtent> = await load(url + '&returnExtentOnly=true', onErr);

      return {
        list,
        extent: extentResponse.extent,
      };
    },
    [baseUrl, token, createQueryString],
  );

  return {
    loadList,
  };
};
